import { createSlice } from '@reduxjs/toolkit';

// The scopes Core acts on, and the reason this list exists rather than a pair of inline
// comparisons. Core normalises an UNKNOWN scope to 'page' itself
// (AssistantController.NormalizeScope), so nothing here has to defend against a bad value —
// but a scope Core DOES accept and this slice drops is a silent loss, and 'account' was
// exactly that until 2026-09-24.
//
// 'page'    — limit changes to the active page (default, safer)
// 'site'    — allow changes across all pages + profile-level settings
// 'account' — the request is about the account itself, not any particular page. Core puts
//             that in the planner's own system prompt ("Do not restrict the plan to the
//             active page"), so dropping it here silently re-narrows an account request
//             back to a page edit.
export const AI_ASSISTANT_SCOPES = ['page', 'site', 'account'];

const initialState = {
  isOpen: false,
  messages: [],
  pendingPrompt: null,
  nextMessageId: 1,
  scope: 'page',
};

export const aiAssistantSlice = createSlice({
  name: 'aiAssistant',
  initialState,
  reducers: {
    aiAssistantOpen: (state, action) => {
      state.isOpen = true;
      if (action.payload) state.pendingPrompt = action.payload;
    },
    aiAssistantClose: (state) => {
      state.isOpen = false;
      state.pendingPrompt = null;
    },
    aiAssistantClearPendingPrompt: (state) => {
      state.pendingPrompt = null;
    },
    aiAssistantSetMessages: (state, action) => {
      state.messages = action.payload;
    },
    aiAssistantAppendMessages: (state, action) => {
      state.messages.push(...action.payload);
    },
    aiAssistantUpdateMessage: (state, action) => {
      const { id, patch } = action.payload;
      const idx = state.messages.findIndex((m) => m.id === id);
      if (idx !== -1) state.messages[idx] = { ...state.messages[idx], ...patch };
    },
    aiAssistantReserveMessageIds: (state, action) => {
      state.nextMessageId += action.payload;
    },
    aiAssistantReset: () => initialState,
    aiAssistantHydrate: (state, action) => {
      const { messages, nextMessageId, scope } = action.payload || {};
      if (Array.isArray(messages)) state.messages = messages;
      if (typeof nextMessageId === 'number' && nextMessageId > state.nextMessageId) {
        state.nextMessageId = nextMessageId;
      }
      if (AI_ASSISTANT_SCOPES.includes(scope)) state.scope = scope;
    },
    aiAssistantSetScope: (state, action) => {
      if (AI_ASSISTANT_SCOPES.includes(action.payload)) state.scope = action.payload;
    },
  },
});

export const {
  aiAssistantOpen,
  aiAssistantClose,
  aiAssistantClearPendingPrompt,
  aiAssistantSetMessages,
  aiAssistantAppendMessages,
  aiAssistantUpdateMessage,
  aiAssistantReserveMessageIds,
  aiAssistantReset,
  aiAssistantHydrate,
  aiAssistantSetScope,
} = aiAssistantSlice.actions;

export const selectAiAssistantIsOpen = (state) => state.aiAssistant.isOpen;
export const selectAiAssistantMessages = (state) => state.aiAssistant.messages;
export const selectAiAssistantPendingPrompt = (state) => state.aiAssistant.pendingPrompt;
export const selectAiAssistantNextMessageId = (state) => state.aiAssistant.nextMessageId;
export const selectAiAssistantScope = (state) => state.aiAssistant.scope;

export default aiAssistantSlice.reducer;
